import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Http, Response } from '@angular/http';
import { SpinnerService } from '@chevtek/angular-spinners';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/timeout';
import { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { RALResponse } from '../models/ralresponse';
import { Project } from '../models/project';

@Injectable()
export class ProjectService {
  constructor(
    private http: Http,
    private httpClient: HttpClient,
    private spinnerService: SpinnerService
  ) {}

  baseurl = environment.apiBaseUrl;

  timeout = 3000;

  // save project into project table when new project is added w.r.t company
  saveProject(company_id, data): Observable<RALResponse> {
    return this.httpClient.post<RALResponse>(`${this.baseurl}/project/save`, { company_id, data });
  }

  // delete entry from project table of project when clicked on trash button
  deleteProject(projectId) {
    return this.http
      .post(`${this.baseurl}/project/delete`, { projectId })
      .map((res: Response) => res.json());
  }

  // get all projects from DB corresponding to company
  getProjects(company_id: string) {
    return this.http
      .post(`${this.baseurl}/project/getProjects`, { company_id })
      .map((res: Response) => res.json());
  }

  // get single selected project from DB
  getProject(project_id: string) {
    return this.http
      .post(`${this.baseurl}/project/getProjectInfo`, { project_id })
      .map((res: Response) => res.json());
  }

  // update fields of project data page onchange event to save them into DB
  onChangeProject(project_id, data) {
    return this.http
      .post(`${this.baseurl}/project/onChange`, { project_id, data })
      .map((res: Response) => res.json());
  }

  // update project data page details and save into DB when click on save button on header
  updateProject(project_id, data) {
    return this.http
      .post(`${this.baseurl}/project/updateProject`, { project_id, data })
      .map((res: Response) => res.json());
  }

  // update value of current/projected/future of bed rate and save in db
  updateBedRates(data) {
    return this.http
      .post(`${this.baseurl}/project/updateBedRates`, { data })
      .map((res: Response) => res.json());
  }

  // when a project is added then demo data ( prefdeined rooms) is added for that project
  saveProjectBasic(project_id: string) {
    return this.http
      .post(`${this.baseurl}/project/addDemoData`, { project_id })
      .map((res: Response) => res.json());
  }

  // return basic data of project (monthly_bed_rate,room type,licensed_beds,revenue etc)
  getProjectBasicData(project_id: string) {
    return this.http
      .post(`${this.baseurl}/project/getProjectBasicInfo`, { project_id })
      .map((res: Response) => res.json());
  }

  // returns room information (room_name,short_name etc)
  getProjectRooms(project_id: string) {
    return this.http
      .post(`${this.baseurl}/project/getProjectRooms`, { project_id })
      .map((res: Response) => res.json());
  }

  // returns type, room id, monthly_bed_rate,licensed_beds
  getProjectRevenues(project_id: string) {
    return this.http
      .post(`${this.baseurl}/project/getProjectRevenues`, { project_id })
      .map((res: Response) => res.json());
  }

  // returns bed related information based on selection (monthly_bed_rate,room type,licensed_beds,revenue etc)
  getBedDetails(selectionType, projectId) {
    return this.http
      .post(`${this.baseurl}/project/getBedDetails`, {
        selectionType,
        projectId,
      })
      .map((res: Response) => res.json());
  }

  // get 120 months record from yearlyrecords table i.e records of 10 year of opex page
  getYearlyRecords(projectId) {
    return this.http
      .post(`${this.baseurl}/project/getYearlyRecords`, {
        projectId,
      })
      .map((res: Response) => res.json());
  }

  // get 2 years data of opex page only for printing
  getYearlyRecordsForPrinting(projectId) {
    return this.http
      .post(`${this.baseurl}/project/getYearlyRecordsForPrinting`, {
        projectId,
      })
      .map((res: Response) => res.json());
  }

  // add an entry into yearlyrecords collection(table) for residents and staff section of project data page
  addYearlyRecord(projectId) {
    return this.http
      .post(`${this.baseurl}/project/addYearlyRecord`, {
        projectId,
      })
      .map((res: Response) => res.json());
  }

  // update values(caregivers_night,caregivers_day and other parameters that are affected with this input) in yearly records table when beds occupied are entered
  updateYearlyRecord(id, data) {
    return this.http
      .post(`${this.baseurl}/project/updateYearlyRecord`, {
        id,
        data,
      })
      .map((res: Response) => res.json());
  }

  // to fill 120 months with the largest value in table when click on repeat button of project data page
  copyAll(data) {
    return this.http
      .post(`${this.baseurl}/project/copyAllRecords`, { data })
      .map((res: Response) => res.json());
  }

  // to fill 120 months with 0 in table when click on clear button of project data page
  clearAll(project_id) {
    return this.http
      .post(`${this.baseurl}/project/clearAllRecords`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // clear all data from bed rates table i.e make it 0 in project data page, when click on clear buttons of current/potential/future bed rates
  clearBedRates(project_id, bed_type) {
    return this.http
      .post(`${this.baseurl}/project/clearBedRates`, {
        project_id,
        bed_type,
      })
      .map((res: Response) => res.json());
  }

  // calculates and save total beds in projectbasic table for current bed rates
  projectCurrentData(project_id) {
    return this.http
      .post(`${this.baseurl}/project/projectCurrentData`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // calculates and save total revenues in projectbasic table for current bed rates
  projectCurrentRevenue(project_id) {
    return this.http
      .post(`${this.baseurl}/project/projectCurrentRevenue`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // calculates and save total beds in projectbasic table for projected bed rates
  projectProjectedData(project_id) {
    return this.http
      .post(`${this.baseurl}/project/projectProjectedData`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // calculates and save total revenues in projectbasic table for projected bed rates
  projectProjectedRevenue(project_id) {
    return this.http
      .post(`${this.baseurl}/project/projectProjectedRevenue`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // calculates and save total beds in projectbasic table for future bed rates
  projectFutureData(project_id) {
    return this.http
      .post(`${this.baseurl}/project/projectFutureData`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // calculates and save total revenues in projectbasic table for future bed rates
  projectFutureRevenue(project_id) {
    return this.http
      .post(`${this.baseurl}/project/projectFutureRevenue`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // to add new room entry in projectRooms collection, when click on add room
  addRoom(project_id) {
    return this.http
      .post(`${this.baseurl}/project/addRoom`, {
        project_id,
      })
      .map((res: Response) => res.json());
  }

  // save name of add other room when update on "onchange" event
  saveRoomName(room_id, value) {
    return this.http
      .post(`${this.baseurl}/project/saveRoomName`, {
        room_id,
        value,
      })
      .map((res: Response) => res.json());
  }

  // this api will hit, when project notes are updated in project data page
  saveProjectNotes(projectId, data) {
    return this.http
      .post(`${this.baseurl}/project/saveProjectNotes`, {
        projectId,
        data,
      })
      .map((res: Response) => res.json());
  }

  // delete entry of room from DB that are added by user, when click on trash button
  removeRoom(room_id) {
    return this.http
      .post(`${this.baseurl}/project/removeRoom`, {
        room_id,
      })
      .map((res: Response) => res.json());
  }

  /* THIS FUNCTION IS USED IN OPERATIONAL EXPENSES PAGE WHERE WE NEED OCCUPIED BEDS */
  getOccupiedBeds(data) {
    return this.http
      .post(`${this.baseurl}/project/getOccupiedBeds`, { data })
      .map((res: Response) => res.json());
  }

  // update all opex fields except 'management_fee', 'rent','taxes','insurance_property','gas','water_sewer','electric','garbage_removal','landscaping_plowing','exterminator','business_license_renewal','accounting_payroll_services','hoa_fees'
  updateOpExpenses(data) {
    return this.http
      .post(`${this.baseurl}/project/updateOpExpenses`, {
        data,
      })
      .map((res: Response) => res.json());
  }

  /* UPDATE TOTAL OPERATING EXPENSES WHICH INCLUDE PAYROLL, GENERAL AND OPERATING EXPENSES */
  updateTotalExpenses(data) {
    return this.http
      .post(`${this.baseurl}/project/updateTotalExpenses`, {
        data,
      })
      .map((res: Response) => res.json());
  }

  /* UPDATE DEBTS FOR YEARLY RECORDS */
  saveDebt(data) {
    return this.http
      .post(`${this.baseurl}/project/saveDebt`, {
        data,
      })
      .map((res: Response) => res.json());
  }

  // UPDATE CAREGIVER WAGES WHEN WORKER COMP FACTOR IS ADDED STARTS HERE
  updateCareGiverWages(data) {
    return this.http
      .post(`${this.baseurl}/project/updateCareGiverWages`, {
        data,
      })
      .map((res: Response) => res.json());
  }
  // UPDATE CAREGIVER WAGES WHEN WORKER COMP FACTOR IS ADDED ENDS HERE

  // UPDATE RESIDENT BEDS IF LICENSED BEDS ARE LESS THAN PREVIOUSLY ADDED STARTS HERE
  updateResidentBeds(data) {
    return this.http
      .post(`${this.baseurl}/project/updateResidentBeds`, {
        data,
      })
      .map((res: Response) => res.json());
  }
  // UPDATE RESIDENT BEDS IF LICENSED BEDS ARE LESS THAN PREVIOUSLY ADDED ENDS HERE

  // not used now
  updateInvetmentEssentials(projectId, data) {
    return this.http
      .post(`${this.baseurl}/project/updateInvestmentEssentials`, {
        projectId,
        data,
      })
      .map((res: Response) => res.json());
  }

  // update startup month for project data page
  updateStartUpMonth(data) {
    return this.http
      .post(`${this.baseurl}/project/updateStartUpMonth`, {
        data,
      })
      .map((res: Response) => res.json());
  }

  // CALCULATE 10 YEARS DATA STARTS HERE
  calculateTenYears(data) {
    return this.http
      .post(`${this.baseurl}/project/calculateTenYears`, { data })
      .map((res: Response) => res.json());
  }
  // CALCULATE 10 YEARS DATA ENDS HERE

  // to check whether business type is "existing" or "start-up", to perform actions in opex page for caregivers entries
  getProjectType(data) {
    return this.http
      .post(`${this.baseurl}/project/getProjectType`, { data })
      .map((res: Response) => res.json());
  }

  copyProject(projectId): Observable<string> {
    return this.httpClient.post<string>(`${this.baseurl}/projects/${projectId}`, null);
    //      .pipe(catchError(this.handleError));
  }

  getProjectV2(projectId): Observable<Project> {
    return this.httpClient.get<Project>(`${this.baseurl}/projects/${projectId}`);
  }

  //  private handleError(error: HttpErrorResponse) {
  //    if (error.error instanceof ErrorEvent) {
  //      // A client-side or network error occurred. Handle it accordingly.
  //      console.error('An error occurred:', error.error.message);
  //    } else {
  //      // The backend returned an unsuccessful response code.
  //      // The response body may contain clues as to what went wrong,
  //      console.error(
  //        `Backend returned code ${error.status}, ` +
  //        `body was: ${error.error}`);
  //    }
  //    // return an observable with a user-facing error message
  //    return throwError('Something bad happened; please try again later.');
  //  };
}



// WEBPACK FOOTER //
// ./src/app/service/project.service.ts